import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, KeyRound, ShieldAlert, Loader2 } from "lucide-react";
import HealthCirclesLogo from "@/components/HealthCirclesLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, email: "" });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const validate = useCallback(async () => {
    if (!token) { setState({ loading: false, valid: false, email: "" }); return; }
    try {
      const { data } = await api.get(`/auth/reset-token/${encodeURIComponent(token)}`);
      setState({ loading: false, valid: true, email: data.email });
    } catch (err) {
      setState({ loading: false, valid: false, email: "" });
    }
  }, [token]);

  useEffect(() => { validate(); }, [validate]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw1 });
      toast.success("Password updated. Please sign in with your new password.");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6 py-12">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center">
            <HealthCirclesLogo size={22} />
          </div>
          <div className="text-lg font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>HealthCircles</div>
        </div>

        {state.loading ? (
          <div className="text-center text-[#8A9992] py-20" data-testid="reset-loading">
            <Loader2 className="inline animate-spin" size={18} /> Validating your reset link…
          </div>
        ) : !state.valid ? (
          <div className="text-center" data-testid="reset-invalid">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#FCE7D9] text-[#B14A2A] flex items-center justify-center mb-6">
              <ShieldAlert size={26} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#B14A2A]" style={{ fontFamily: "Instrument Sans" }}>
              Link is invalid or expired
            </h1>
            <p className="mt-3 text-sm text-[#47554D]">
              Reset links are valid for 30 minutes. Request a new one to continue.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/forgot-password" className="btn-emerald">Request new link</Link>
              <Link to="/login" className="btn-outline-emerald">Back to sign in</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center mb-6">
              <KeyRound size={26} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A4C36] text-center" style={{ fontFamily: "Instrument Sans" }}>
              Choose a new password
            </h1>
            <p className="mt-3 text-sm text-center text-[#47554D]">
              For <b className="text-[#1F2A24]">{state.email}</b>
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="field-label block mb-2">New password</label>
                <input
                  data-testid="reset-pw1"
                  required
                  type="password"
                  minLength={8}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  className="input-clean"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="field-label block mb-2">Confirm password</label>
                <input
                  data-testid="reset-pw2"
                  required
                  type="password"
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="input-clean"
                  placeholder="Type it again"
                />
                {pw2 && pw1 !== pw2 && (
                  <div className="text-[11px] text-[#B14A2A] mt-1.5">Passwords do not match yet.</div>
                )}
              </div>
              <button type="submit" disabled={busy || !pw1 || pw1 !== pw2} className="btn-emerald w-full justify-center" data-testid="reset-submit">
                {busy ? "Updating…" : (<>Update password <ArrowRight size={16} /></>)}
              </button>
            </form>

            <div className="mt-6 text-sm text-center text-[#6C7B72]">
              <Link to="/login" className="text-[#0A4C36] font-semibold hover:underline">Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
